/**
 * EventPeriodBanner — "Day 3 of 7 — State Fair 2026"
 *
 * Shows when today falls within the event period date range.
 * Dismissible per session (sessionStorage).
 */

import { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { useTranslation } from "react-i18next";
import { differenceInDays, parseISO, isWithinInterval, startOfDay } from "date-fns";

interface EventPeriodBannerProps {
  eventPeriod: {
    name: string | null;
    startDate: string;
    endDate: string;
  } | null;
  mapId: string;
}

export function EventPeriodBanner({ eventPeriod, mapId }: EventPeriodBannerProps) {
  const { t } = useTranslation("map");
  const storageKey = `eventPeriod-dismissed-${mapId}`;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });

  const periodInfo = useMemo(() => {
    if (!eventPeriod) return null;

    const today = startOfDay(new Date());
    const start = startOfDay(parseISO(eventPeriod.startDate));
    const end = startOfDay(parseISO(eventPeriod.endDate));

    if (!isWithinInterval(today, { start, end })) return null;

    const current = differenceInDays(today, start) + 1;
    const total = differenceInDays(end, start) + 1;

    return { current, total, name: eventPeriod.name };
  }, [eventPeriod]);

  if (!periodInfo || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(storageKey, "1");
    } catch {
      // ignore
    }
  };

  const text = periodInfo.name
    ? t("eventPeriod.dayOfNamed", {
        current: periodInfo.current,
        total: periodInfo.total,
        name: periodInfo.name,
        defaultValue: `Day ${periodInfo.current} of ${periodInfo.total} — ${periodInfo.name}`,
      })
    : t("eventPeriod.dayOf", {
        current: periodInfo.current,
        total: periodInfo.total,
        defaultValue: `Day ${periodInfo.current} of ${periodInfo.total}`,
      });

  return (
    <div className="pointer-events-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background/80 backdrop-blur-sm border shadow-sm text-xs font-medium text-foreground">
      <span>{text}</span>
      <button
        type="button"
        onClick={handleDismiss}
        className="p-0.5 rounded-full hover:bg-muted transition-colors"
        aria-label={t("eventPeriod.dismiss", { defaultValue: "Dismiss" })}
      >
        <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
      </button>
    </div>
  );
}
